import React, {useEffect, useRef, useState} from 'react';
import {IElementQuestionMatchPair, IMatchPairQuestion} from '../../../../../incase-domain/elements/question/questionMatchPair';
import {checkIfPairIsCorrect} from '../../../../../util/matchPairs.utils';
import {Pair} from './MatchPairQuestionElement';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {getBorderStyle} from '../../../../../util/style.utils';
import {getElementScale} from '../../../../../util/domScale';

export const colors = ['#6D26C7', '#D79325', '#44A0D4', '#CCA2DD', '#94DBA6', '#E2D081', '#81E2D3', '#E894B0', '#A5A623', '#28A71C'];
const incorrectColor = '#C32323';

interface MatchedMatchPairElement {
    id: string;
    text: string;
    isSelected: boolean;
    isMatched: boolean;
    isCorrect: boolean;
    color: string;
}

interface MatchPairsQuestionElementsProps {
    element: IElementQuestionMatchPair;
    selectedPairs: {leftSelectedElement: string; rightSelectedElement: string}[];
    answered: boolean;
    wrongAnswer?: Pair;
    handleItemClick: (id: string, position: keyof Pair) => void;
    device?: DeviceType;
}

function MatchPairsQuestionElementItem(props: MatchPairsQuestionElementsProps): JSX.Element | null {
    const {element, selectedPairs, answered, wrongAnswer, handleItemClick, device} = props;

    const leftRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
    const rightRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
    const parentContainerRef = useRef<HTMLDivElement | null>(null);

    const [lines, setLines] = useState<{d: string; color: string}[]>([]);

    const mapItems = (items: IMatchPairQuestion[]): MatchedMatchPairElement[] => {
        return items
            .filter((item, index, self) => self.findIndex((t) => t.text === item.text) === index)
            .map((item) => {
                const index = selectedPairs.findIndex((x) => x.leftSelectedElement === item.id || x.rightSelectedElement === item.id);
                const pair = selectedPairs[index];
                return {
                    ...item,
                    isSelected: !!pair?.leftSelectedElement || !!pair?.rightSelectedElement,
                    isMatched: !!pair?.leftSelectedElement && !!pair?.rightSelectedElement,
                    isCorrect: checkIfPairIsCorrect(element, pair?.leftSelectedElement, pair?.rightSelectedElement),
                    color: colors[index % colors.length],
                };
            });
    };

    const getColor = (item: MatchedMatchPairElement): string => {
        if (item.id === wrongAnswer?.leftSelectedElement || item.id === wrongAnswer?.rightSelectedElement) {
            return incorrectColor;
        }
        if (answered) {
            if (item.isCorrect) {
                return 'darkgreen';
            }
            return incorrectColor;
        }
        if (item.isMatched || item.isSelected) {
            return item.color;
        }
        return '#d2d2d2';
    };

    const calculateLines = (): void => {
        const newLines: {d: string; color: string}[] = [];

        const parentEl = parentContainerRef.current;
        const parentRect = parentEl?.getBoundingClientRect();
        if (!parentEl || !parentRect) return;

        const {scaleX, scaleY} = getElementScale(parentEl, undefined, undefined, parentRect);

        selectedPairs.forEach((pair) => {
            const leftItem = leftRefs.current.get(pair.leftSelectedElement);
            const rightItem = rightRefs.current.get(pair.rightSelectedElement);

            if (leftItem && rightItem) {
                const leftRect = leftItem.getBoundingClientRect();
                const rightRect = rightItem.getBoundingClientRect();

                const leftX = (leftRect.right - parentRect.left) * scaleX;
                const leftY = (leftRect.top + leftRect.height / 2 - parentRect.top) * scaleY;
                const rightX = (rightRect.left - parentRect.left) * scaleX;
                const rightY = (rightRect.top + rightRect.height / 2 - parentRect.top) * scaleY;

                const d = `M ${leftX} ${leftY} C ${leftX + 50 * scaleX} ${leftY}, ${rightX - 50 * scaleX} ${rightY}, ${rightX} ${rightY}`;

                const leftItemMapped = mapItems(element.leftElement).find((item) => item.id === pair.leftSelectedElement);
                const rightItemMapped = mapItems(element.rightElement).find((item) => item.id === pair.rightSelectedElement);
                let color;

                if (leftItemMapped && rightItemMapped) {
                    color = getColor(leftItemMapped || rightItemMapped);
                }

                if (color) {
                    newLines.push({
                        d,
                        color,
                    });
                }
            }
        });

        setLines(newLines);
    };

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            calculateLines();
        });

        if (parentContainerRef.current) {
            resizeObserver.observe(parentContainerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPairs, answered]);

    const isDisabled = (item: MatchedMatchPairElement): boolean => {
        const isItemInLeftColumn = element.pairs.map((pair) => pair.leftElementId).includes(item.id);

        return answered || (!element.haveTryButton && item.isCorrect && isItemInLeftColumn);
    };

    const generateColumn = (items: IMatchPairQuestion[], position: keyof Pair): JSX.Element => {
        const answerDataClass =
            position === 'leftSelectedElement'
                ? 'c-matchPairQuestionElement__answer_data--left'
                : 'c-matchPairQuestionElement__answer_data--right';

        const textClass =
            position === 'leftSelectedElement' ? 'c-matchPairQuestionElement__text--left' : 'c-matchPairQuestionElement__text--right';

        return (
            <div className="c-matchPairQuestionElement__answer">
                {mapItems(items).map((item) => (
                    <button
                        type="button"
                        ref={(el) => {
                            if (el) {
                                if (position === 'leftSelectedElement') {
                                    leftRefs.current.set(item.id, el);
                                } else {
                                    rightRefs.current.set(item.id, el);
                                }
                            }
                        }}
                        key={item.id}
                        style={{
                            borderRadius: getBorderStyle(element.borderRadius),
                            fontFamily: element.fontFamily?.name,
                        }}
                        className={`c-matchPairQuestionElement__answer_data ${answerDataClass}`}
                        disabled={isDisabled(item)}
                        onClick={() => handleItemClick(item.id, position)}
                    >
                        <input
                            type="radio"
                            id={item.id}
                            name={position}
                            checked={item.isSelected}
                            className="c-matchPairQuestionElement__radio"
                            style={{
                                backgroundColor: getColor(item),
                            }}
                            disabled={isDisabled(item)}
                            onChange={() => handleItemClick(item.id, position)}
                        />
                        <span
                            className={`c-matchPairQuestionElement__text ${textClass}`}
                            style={{
                                fontSize: `${element.fontSize || 'initial'}em`,
                            }}
                        >
                            {item.text}
                        </span>
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div
            ref={parentContainerRef}
            className={`c-matchPairQuestionElement ${device === DeviceType.MOBILE ? 'c-matchPairQuestionElement--mobile' : ''}`}
            style={{position: 'relative'}}
        >
            {generateColumn(element.leftElement, 'leftSelectedElement')}
            <svg className="c-matchPairQuestionElement__lines">
                {lines.map((line, index) => (
                    <path key={index} d={line.d} stroke={line.color} strokeWidth="2" fill="transparent" />
                ))}
            </svg>
            {generateColumn(element.rightElement, 'rightSelectedElement')}
        </div>
    );
}

export default MatchPairsQuestionElementItem;
